import Base62 from '@pluve/base62';
import { StaticToast } from 'src/components/common';
import { SupplierEnum } from 'subPackages/szPhysical/pages/enum';
import { cookies, Deserialize, storage } from '.';
import { jumpBeforeAuth, jumpToAppletPublicMethod } from './auth';
import { bestPaySimpleCheckout } from './bestpay';
import { DEPLOY__ENV, getHyMallHostname, webviewURL } from './env';
import { fetchJson } from './fetch';
import { xflowPushEvent } from './pageTrack';
import { CashierPayMethod, PrescriptionStatus, THIRD_PLATFORM_RESOURCECODE } from './staticData';
import { filterEmptyValueInObject, fullyDecodeURI, isDrugReplenishType, isJSONString, isYSB } from './tool';
import validate from './validate';

interface umpPayParams {
  /**
   * thirdOrderId: UMP 订单 id
   */
  orderId: string;

  /**
   * 支付成功后回跳地址
   */
  redirectUrl?: string;
}

interface cashierPayParams {
  orderNo: string;
  [key: string]: number | string | object | boolean;
}

interface commonPayParamsType {
  orderRealAmount: number,
  orderNo: string,
  orderType: string,
  id?: string, // 服务包支付没有id
  orderTime?: string, // 服务包支付没有orderTime
  thirdOrderId?: string,
  platformCode?: string,
  servpackBizType?: string,
  relatedBizNo?: string,
  returnUrl?: string, // 小程序回跳地址
  canBack?: boolean,
  h5ZACashierPayReturnUrl?: string, // 收银台回跳地址
  h5ZACashierIsReplace?: boolean, // 收银台跳转路由是否是replace
  actTag?: string,  // 服务包购买参数
  inquiryNo?: string, // 问诊参数
  inquiryType?: string, // 问诊参数
  payInquiryType?: string, // 问诊参数 text/video
  inquiryTimingType?: string, // 问诊参数 预约单相关
  serviceContractNo?: string, // 问诊参数 预约单相关
  cashierPayMethod?: CashierPayMethod, // 支付类型 收银台支付类型
  businessEntry?: any[],
  orderGoods?: any[],
}

const THIRD_PAY_PLATFORM_LIST = [THIRD_PLATFORM_RESOURCECODE.KYUSHU_POP, THIRD_PLATFORM_RESOURCECODE.UNION_DRUG, THIRD_PLATFORM_RESOURCECODE.YIFENG];

export const drugIsFromShop = (order) => {
  const { platformCode, orderType, channelOrderNo } = order;
  // DRUGS 大写drugs表示三方过来的,channelOrderNo有值也表示三方的
  if([THIRD_PLATFORM_RESOURCECODE.ZA_HAOYAOSHI, THIRD_PLATFORM_RESOURCECODE.ZA_UMBRELLA, THIRD_PLATFORM_RESOURCECODE.ZA_MALL, THIRD_PLATFORM_RESOURCECODE.HY_MALL].indexOf(platformCode) >= 0) {
    if(orderType === 'DRUGS' || !!channelOrderNo) {
      return true;
    }
  }
  return false;
};

/** 最福利支付 */
export const zuifuliPay = (options: cashierPayParams) => {
  const { orderNo = '', returnUrl = '' } = options;
  if (!orderNo) {
    StaticToast.error('缺少订单号');
    return;
  }
  fetchJson({
    type: 'POST',
    url: `/api/api/v1/patient/zuifuli/pay?orderNo=${orderNo}`,
    needLoading: true,
  }).then((res) => {
    if (res && res.code === '0') {
      window.reactHistory.replace(returnUrl);
    } else {
      StaticToast.error(res.message);
    }
  });
};

// 众安收银台支付
export const h5ZACashierPay = (options: cashierPayParams) => {
  const { orderNo = '', returnUrl, cashierPayMethod } = options;
  if (!orderNo) {
    StaticToast.error('缺少订单号');
    return;
  }

  const zaCashierPayApi = cashierPayMethod === CashierPayMethod.MIXED_PAY
    ? '/api/api/v1/patient/mix/pay/submitPay'
    : '/api/api/v1/patient/zaCashier/pay/submitCashierPayV2';

  const zaCashierPayRequestMethod = cashierPayMethod === CashierPayMethod.MIXED_PAY ? 'POST' : 'GET';

  console.log('returnUrl', returnUrl);
  fetchJson({
    type: zaCashierPayRequestMethod,
    url: zaCashierPayApi,
    isloading: true,
    data: {
      orderNo,
      returnUrl,
    },
  }).then((res) => {
    console.log('zaPay返回结果', res);
    const { result = '', code = '' } = res;
    if (code === '0') {
      handleZaFormPay(result);
    }
  });
};

const defaultRedirectUrl = `${webviewURL}/hospital/myorder`;

export function umpPay({ orderId, redirectUrl = defaultRedirectUrl }: umpPayParams) {
  const thirdOrigin = 'https://vc-stg.eatonclinic.com:8844/api/order/htmlopen/payment';
  const redirect = encodeURIComponent(redirectUrl || defaultRedirectUrl);
  const payUrl = `${thirdOrigin}?orderId=${orderId}&redirectUrl=${redirect}`;
  storage.set('ump_pay_url', payUrl, 5);
  location.href = payUrl;
}

// 九州通&药联支付
export function popAndUnionPay({ platformCode, thirdOrderId, orderRealAmount, id, orderNo, orderGoods = [] }: commonPayParamsType) {
  if(platformCode === THIRD_PLATFORM_RESOURCECODE.YIFENG) {
    const yifengPayUrl = DEPLOY__ENV({
      tst: 'https://yf-test-oss.yifengx.com/webtest/alkaid/pay/index.html#/index',
      uat: 'https://yf-test-oss.yifengx.com/webtest/alkaid/pay/index.html#/index',
      prd: 'https://open.yifengx.com/rest/alkaid/pay/index.html#/index',
    });
    const rxList = orderGoods.filter((item) => !!item.ext);
    let hasRx = false;
    try {
      rxList.forEach((item) => {
        const rx = JSON.parse(item.ext || '{}');
        if(rx.isRx === 'Y') {
          hasRx = true;
        }
      });
    } catch(e) {
    }
    // 支付参数
    const params = {
      merchantId: '5c49b2b72adc9100015f02ca',
      appId: 'ZAYSB',
      orderCode: orderNo,
      payBillCode: orderNo,
      amount: orderRealAmount * 100,
      version: 'v2',
      openId: validate.isFromOwnMiniApplet() ? cookies.get('openId') : undefined,
      appRedirectUrl: `${window.location.origin}/hospital/payordersuccess/ysb?id=${id}&type=${hasRx ? 'rx' : ''}`,
    };
    // 最终支付 url
    const payUrl = `${yifengPayUrl}?params=${Base62.encode(JSON.stringify(params))}`;
    if(window.location.href.indexOf('hospital/solidMedicine/order/confirm') > -1) {
      window.location.replace(payUrl);
      return;
    }
    window.location.href = payUrl;
    return;
  }
  if (!thirdOrderId) {
    StaticToast.error('缺少订单号');
    return;
  }
  fetchJson({
    type: 'post',
    url: '/api/api/v1/patient/order/third/prepay/h5',
    isloading: true,
    data: {
      thirdOrderId,
      platformCode,
      payEnv: 'ZA_CASHIER',
      payAmount: orderRealAmount,
    },
  }).then((res) => {
    console.info('九州通/药联返回结果', res);
    const { result = {}, code = '' } = res;
    if (code === '0') {
      location.href = result.backUrl;
    } else {
      res.message && StaticToast.error(res.message);
    }
  });
}

// 微信小程序支付
export function miniProgramPay(order: any) {
  const { orderType, orderRealAmount, platformCode, vasCode, thirdOrderId, orderTime, expireTime, orderNo, isFamilyDoctorServpack = false, id, inquiryNo, inquiryType, payInquiryType, inquiryTimingType, actTag, canBack } = order;
  if (orderRealAmount === 0) {
    if (validate.isFromOwnMiniApplet()) {
      if (platformCode === SupplierEnum.KD) {
        wx.miniProgram.redirectTo({
          url: `/pages/webview?src=${encodeURIComponent(`/hospital/sub/szPhysical/physicalPaySuccess?orderNo=${orderNo}`)}`,
        });
        return;
      }
      wx.miniProgram.redirectTo({
        url: `/pages/webview?src=${encodeURIComponent('/hospital/myorder')}`,
      });
    } else {
      if (platformCode === SupplierEnum.KD) {
        window.reactHistory.replace({
          pathname: '/hospital/sub/szPhysical/physicalPaySuccess',
          search: `orderNo=${orderNo}`,
        });
        return;
      }
      window.reactHistory.replace({
        pathname: '/hospital/myorder',
      });
    }
    return ;
  }
  if(validate.isFromZabxMiniApplet()){
    ZABXMiniAppPay(order);
    return ;
  }else{
    console.log('channelResourceCode', cookies.get('channelResourceCode'));
  }
  if (platformCode && THIRD_PAY_PLATFORM_LIST.includes(platformCode)) {
    // 九州通支付，跳转小程序里供三方支付页
    const popPay: any = {
      price: orderRealAmount,
      orderId: thirdOrderId,
      orderTime,
      expireTime,
      orderNo,
      platformCode,
      orderType,
    };

    if(isYSB(vasCode)) {
      popPay.returnUrl = encodeURIComponent(`/hospital/payordersuccess/ysb?id=${id}`);
    }

    const popPayParams = JSON.stringify(popPay);
    const path = `/pages/commonThirdPay?payParams=${popPayParams}`;
    jumpToAppletPublicMethod(path, orderNo, canBack);
  } else {
    let payData: any = {
      payType: orderType,
      orderRealAmount,
      orderNo,
      orderTime,
      isFamilyDoctorServpack,
      orderId: id,
      inquiryNo,
      inquiryType,
      payInquiryType,
      inquiryTimingType,
      actTag,
    };
    payData = filterEmptyValueInObject(payData);
    // 添加魔镜支付
    if(orderType === 'signDetection' || orderType === 'skinTextureTest') {
      payData.returnUrl = encodeURIComponent(`/hospital/examination/entry?orderNo=${orderNo}`);
    }
    // 通用支付
    const payParams = JSON.stringify(payData);
    const path = `/pages/pay?payParams=${payParams}`;
    jumpToAppletPublicMethod(path, orderNo, canBack);
  }
}

// 众安保险小程序支付
const ZABXMiniAppPay = (params) => {
  console.log('openId',  cookies.get('openId'));
  // 保险小程序传过来的openId是有特殊字符的
  let openId = cookies.get('openId') || '';
  openId = encodeURIComponent(openId);
  const backURl = getReturnUrl(params, window.location.origin);
  fetchJson({
    type: 'GET',
    url: '/api/api/v1/patient/zaCashier/pay/submitPixiuCashierPay',
    data: {
      orderNo: params.orderNo,
      actualOpenId: openId,
      returnUrl: backURl,
    },
    needLoading: true,
  }).then((res) => {
    if(res && res.code === '0'){
      if(res.result){
        try{
          const result = JSON.parse(res.result);
          if(result.value && result.value.domain){
            const domain = JSON.parse(result.value.domain);
            const payParams ={
              nonce_str: domain.nonceStr,
              package_str: domain.package,
              pay_sign: domain.sign,
              sign_type: domain.signType,
              timestamp: domain.timeStamp,
            };
            const payParamsStr = JSON.stringify(payParams);
            console.log('payPamras',payParamsStr);
            console.log('backUrl',backURl);
            wx && wx.miniProgram.navigateTo({
              url: `/pages/subject/webviewPay/index?data=${encodeURIComponent(payParamsStr)}&backUrl=${encodeURIComponent(backURl)}`,
            });
          }
        }catch(err){
          console.log('err',err);
        }

      }else{
        console.log('res',res);
      }
    }else{
      console.log('res',res);
    }
  });
};


const h5BuyDrugPay = (params: commonPayParamsType) => {
  const {
    platformCode, thirdOrderId, orderNo = '',
    orderRealAmount, orderType = '', id = '',
    h5ZACashierIsReplace = false, businessEntry = [],
    inquiryNo,
    cashierPayMethod = '',
    returnUrl = '',
  } = params;
  console.info('0、进入收银台逻辑', params);
  if (orderRealAmount === 0) {
    if (['employeeCheckup', 'healthCheck'].includes(orderType)) {
      window.reactHistory.replace({
        pathname: '/hospital/sub/szPhysical/physicalPaySuccess',
        search: `orderNo=${orderNo}`,
      });
      return;
    }
    window.reactHistory.replace(platformCode === THIRD_PLATFORM_RESOURCECODE.YIFENG ? `/hospital/payordersuccess/ysb?id=${id}&type=${businessEntry.length > 0 ? 'rx' : ''}` : '/hospital/myorder');
    return ;
  }
  if (platformCode && THIRD_PAY_PLATFORM_LIST.includes(platformCode)) {
    console.info('0.1、进入 popAndUnion支付',);
    popAndUnionPay(params);
  } else {
    console.log('H5走众安收银台支付');
    let h5ZACashierPayReturnUrl =  '';
    // 购买洁众洁牙服务的支付成功页不一样，独立出来
    if(orderType === 'teethCleanServices'){
      h5ZACashierPayReturnUrl = `${location.origin}/hospital/clinic/paysuccess?orderId=${thirdOrderId || id}`;
    }else if(orderType === 'healthCheck' || orderType === 'dentalServices' || orderType === 'productAppoint'){
      // 服务商品去一个支付成功页
      h5ZACashierPayReturnUrl = `${location.origin}/hospital/physicalExamination/paysuccess`;
    } else if(orderType === 'signDetection' || orderType === 'skinTextureTest') {
      h5ZACashierPayReturnUrl = `${location.origin}/hospital/examination/entry`;
    } else if(orderType === 'productServpack') {
      h5ZACashierPayReturnUrl = `${location.origin}/hospital/group/paysuccess?orderId=${thirdOrderId || id}`;
    } else if(orderType === 'inquiry') {
      h5ZACashierPayReturnUrl = `${location.origin}/hospital/paysuccess?inquiryNo=${inquiryNo}`;
    } else if (['employeeCheckup', 'healthCheck'].includes(orderType)) {
      h5ZACashierPayReturnUrl = `${window.location.origin}/hospital/sub/szPhysical/physicalPaySuccess?orderNo=${orderNo}`;
    } else{
      // 其他去一个服务成功页，目前就是服务包的支付成功页
      h5ZACashierPayReturnUrl = `${location.origin}/hospital/static/hpdetection/paysuccess`;
    }

    if (cashierPayMethod === CashierPayMethod.ZUIFULI_PAY) {
      zuifuliPay({
        orderNo,
        // 写死了回跳地址是体检成功页面
        returnUrl: `/hospital/sub/szPhysical/physicalPaySuccess?orderNo=${orderNo}`,
      });
      return;
    }

    // 优先使用外部传递的returnUrl
    h5ZACashierPay({
      orderNo,
      returnUrl: params.returnUrl ? fullyDecodeURI(params.returnUrl) : h5ZACashierPayReturnUrl,
      h5ZACashierIsReplace,
      cashierPayMethod,
    });
  }
};
export const aliPay = (params: commonPayParamsType) => {
  const { platformCode, orderNo } = params;
  let payParams  = {
    orderRealAmount: params.orderRealAmount,
    orderType: params.orderType,
    orderNo: params.orderNo,
    orderTime: params.orderTime,
    isFamilyDoctorServpack: params.servpackBizType && ['3','4','5'].includes(params.servpackBizType),
    inquiryNo: params.inquiryNo,
    inquiryType: params.inquiryType,
    actTag: params.actTag,
  };
  payParams = filterEmptyValueInObject(params);
  if (payParams.orderRealAmount === 0) {
    if (platformCode === SupplierEnum.KD) {
      my.redirectTo({
        url: `/pages/webview/index?src=${encodeURIComponent(`/hospital/sub/szPhysical/physicalPaySuccess?orderNo=${orderNo}`)}`,
      });
      return;
    }
    my.redirectTo({
      url: `/pages/webview/index?src=${encodeURIComponent('/hospital/myorder')}`,
    });
  } else {
    my.navigateTo({
      url: `/pages/pay/index?payParams=${JSON.stringify(payParams)}`,
    });
  }
};
export const bestPay = (params) => {
  const {orderRealAmount, orderNo } = params;
  if (orderRealAmount === 0) {
    window.reactHistory.replace('/hospital/myorder');
    return ;
  }
  if(!orderNo){
    console.log('bestPay params', params);
    StaticToast.error('翼支付缺少订单号!');
    return ;
  }
  fetchJson({
    type: 'post',
    url: '/api/api/v1/patient/bestpay/prePay',
    isloading: true,
    data: {
      orderNo,
      phoneType: validate.isIos() ? 'ios_4.0' : 'android_4.0',
    },
  }).then((res) => {
    if(res.code === '0'){
      if(res.result?.map?.param){
        let bestOptions = res.result.map.param;
        if(isJSONString(bestOptions)){
          bestOptions = JSON.parse(res.result.map.param);
        }
        bestPaySimpleCheckout(bestOptions, params);
      }else{
        console.log('翼支付prePay res',res);
      }
    }else{
      console.log('翼支付prePay报错',res);
    }
  });


};

export const unionPay = (params: commonPayParamsType) => {
  const {orderRealAmount, orderNo, orderTime, relatedBizNo, servpackBizType} = params;
  const isFamilyDoctorServpack = servpackBizType && ['3','4','5'].includes(servpackBizType);
  window.reactHistory.replace({
    pathname: '/hospital/unionpay',
    search: `orderRealAmount=${orderRealAmount}&orderNo=${orderNo}&orderTime=${orderTime}&inquiryNo=${relatedBizNo}&isFamilyDoctorServpack=${isFamilyDoctorServpack}`,
  });
};

export const getReturnUrl = (payParams, origin = '') => {
  let returnUrl = '';
  if (payParams.orderType === 'inquiry') {
    const isAppointment = payParams.inquiryTimingType === 'appointment'; // 视频预约单
    returnUrl = `${origin}/hospital/${isAppointment ? 'specialist/booking/success' : 'paysuccess'}?inquiryNo=${payParams.inquiryNo}&inquiryId=${payParams.inquiryId}`;
  } else if (payParams.orderType === 'nucleicAcidTest') {
    returnUrl = `${origin}/hospital/service/natrsuccess`;
  } else if (payParams.orderType === 'healthCheck' || payParams.orderType === 'dentalServices' || payParams.orderType === 'productAppoint') {
    returnUrl = `${origin}/hospital/physicalExamination/paysuccess`;
  } else if (payParams.orderType === 'sellingServpack') {
    returnUrl = `${origin}/hospital/static/hpdetection/paysuccess`;
  } else if (payParams.orderType === 'teethCleanServices') {
    returnUrl = `${origin}/hospital/clinic/paysuccess?orderId=${payParams.id}`;
  } else if (payParams.orderType === 'productServpack') {
    returnUrl = `${origin}/hospital/group/paysuccess?orderId=${payParams.id}`;
  } else {
    returnUrl = `${origin}/hospital/payordersuccess`;
  }
  return payParams.returnUrl || returnUrl;
};


// 通用支付入口，分不同环境进行分发
export const commonPay = (orderResult: commonPayParamsType) => {
  if (validate.isAlipayApplet()) {
    // 支付宝小程序支付
    aliPay(orderResult);
    // 微信小程序内非益丰购药
  } else if (validate.isFromMiniApplet() && orderResult.platformCode !== THIRD_PLATFORM_RESOURCECODE.YIFENG) {
    // 微信小程序支付
    miniProgramPay(orderResult);
  } else if(validate.isBestpay()){
    // 翼支付App环境支付
    bestPay(orderResult);
  } else if (validate.isFromUnionPay()) {
    // 云闪付环境支付
    unionPay(orderResult);
  } else {
    // h5环境
    h5BuyDrugPay(orderResult);
  }
};

/** 处理众安表单提交支付 */
export const handleZaFormPay = (formDom) => {
  // 添加之前先删除一下，如果单页面，页面不刷新，添加进去的内容会一直保留在页面中，二次调用form表单会出错
  const divForm = document.getElementsByTagName('zaCashierForm');
  if (divForm.length) {
    document.body.removeChild(divForm[0]);
  }
  const div = document.createElement('zaCashierForm');
  div.innerHTML = formDom; // data就是接口返回的form 表单字符串
  document.body.appendChild(div);
  // 触发众安form表单。通过表单id访问
  (document.forms as any).cashierSubmit.submit();
};

/**
 * 解析URL参数工具函数
 *
 * @param url 需要解析的URL字符串
 * @returns 解析后的URLSearchParams对象
 */
const parseUrlParams = (url: string): URLSearchParams => {
  const queryPart = decodeURIComponent(url).split('?')[1] || '';
  return new URLSearchParams(queryPart);
};

/**
 * 处理跳转到商城收银台的逻辑
 *
 * @param {Object} params - 参数对象
 * @param {string} params.mainBucketNo - 主申请单号，用于对应的购物车信息
 * @param {string} params.subBucketNo - 子订单号，用于商城收银台支付
 * @param {PrescriptionStatus} params.prescriptionStatus - 处方状态
 * @returns {void}
 *
 * 如果子订单号为空，则提示错误
 * 如果处方状态为"支付中"，则跳转到商城订单列表页
 * 否则，显示跳转到商城收银台的提示（目前为临时实现）
 */
export const toHyMallCashierPay = ({
  mainBucketNo,
  subBucketNo,
  prescriptionStatus,
}) => {
  if (!subBucketNo) {
    StaticToast.error('toHyMallCashierPay subBucketNo is empty');
    return;
  }
  if (!mainBucketNo) {
    StaticToast.error('toHyMallCashierPay mainBucketNo is empty');
    return;
  }
  if (prescriptionStatus === PrescriptionStatus.PAYING) {
    const hyMallOrderListUrl = '/order/list?orderStatus=init';
    // 支付中，跳转商城订单列表
    jumpBeforeAuth({
      partnerCode: THIRD_PLATFORM_RESOURCECODE.HY_MALL,
      businessType: 'index',
      target: hyMallOrderListUrl,
    });
    return;
  }
  const prescriptionJumpLinkInfo = storage.get(`prescriptionJumpLinkInfo_${mainBucketNo}`);
  if (!prescriptionJumpLinkInfo) {
    StaticToast.error('请从购药页面，发起买药');
    return;
  }
  const { subjectGroupUrl = '', failUrl = '' } = JSON.parse(prescriptionJumpLinkInfo);

  // 解析商城透传的 failUrl
  const failUrlParams = parseUrlParams(failUrl);

  // 获取商城透传的 subjectGroupUrl
  const urlParams = parseUrlParams(subjectGroupUrl);

  // 获取URL参数，提供默认值
  const getParam = (params: URLSearchParams, key: string): string => params.get(key) || '';

  // 获取各参数
  const groupId = getParam(urlParams, 'groupId');
  const channelPoint = getParam(failUrlParams, 'channelPoint');
  const from = getParam(failUrlParams, 'from');
  const couponIds = getParam(failUrlParams, 'couponIds');
  const goodsId = getParam(failUrlParams, 'goodsId');

  const hyMallOrderConfirmUrl = `/order/confirm?from=${from}&groupId=${groupId}&goodsId=${goodsId}&shoppingBasketNos=${subBucketNo}&couponIds=${couponIds}`;

  // 未下单，跳转商城确认订单
  jumpBeforeAuth({
    partnerCode: THIRD_PLATFORM_RESOURCECODE.HY_MALL,
    businessType: 'index',
    target: hyMallOrderConfirmUrl,
    channelPoint,
  });
};

/**
 * 处理跳转到互医商城购物车的操作
 *
 * @param mainBucketNo 主购物篮编号
 * @returns 如果主购物篮编号为空，则显示警告并返回undefined
 */
export const toHyMallCart = (mainBucketNo: string) => {
  if (!mainBucketNo) {
    StaticToast.error('toHyMallCart mainBucketNo is empty');
    return;
  }
  const prescriptionJumpLinkInfo = storage.get(`prescriptionJumpLinkInfo_${mainBucketNo}`) || {};
  if (!prescriptionJumpLinkInfo) {
    StaticToast.error('toHyMallCart prescriptionJumpLinkInfo is empty');
    return;
  }
  const { subjectGroupUrl, failUrl = '' } = JSON.parse(prescriptionJumpLinkInfo);

  // 解析商城透传的 failUrl
  const failUrlParams = parseUrlParams(failUrl);

  const channelPoint = failUrlParams.get('channelPoint') || '';

  if (subjectGroupUrl) {
    jumpBeforeAuth({
      partnerCode: THIRD_PLATFORM_RESOURCECODE.HY_MALL,
      businessType: 'custom',
      target: decodeURIComponent(subjectGroupUrl),
      channelPoint,
    });
  } else {
    StaticToast.error('toHyMallCart subjectGroupUrl is empty');
  }
};

interface MallGroupPurchaseParams {
  /** 问诊详情信息 */
  inquiryDetail: any;
  /** 处方单号 */
  prescriptionNo: string;
  /** 成功回调函数 */
  onSuccess?: (link: string) => void;
  /** 错误回调函数 */
  onError?: (error: any) => void;
}

/**
 * 处理商城购药逻辑
 * 当存在 mallGroupId 并且不是isHyMallDrugReplenish时，调用商城购药链接接口并跳转
 *
 * @param params 参数对象
 * @returns 如果存在 mallGroupId 并且不是isHyMallDrugReplenish则返回 true，否则返回 false
 */
export const handleMallGroupPurchase = (params: MallGroupPurchaseParams): boolean => {
  const { inquiryDetail, prescriptionNo, onSuccess, onError } = params;

  // 从 inquiryDetail 获取必要参数
  const mallGroupId = inquiryDetail?.mallGroupId;
  const inquiryNo = inquiryDetail?.inquiryNo || '';

  // 判断是否为商城的先方后药的问诊单
  const isDrugReplenish = inquiryDetail?.inquiryTimingType ? isDrugReplenishType(inquiryDetail.inquiryTimingType) : false;
  const isHyMallDrugReplenish = isDrugReplenish && !inquiryDetail?.isYSB;

  // 只有当不是isHyMallDrugReplenish并且有mallGroupId时，才处理mallGroupId逻辑
  if (mallGroupId && !isHyMallDrugReplenish) {
    fetchJson({
      type: 'POST',
      url: '/api/api/v1/patient/inquiry/mall/purchaseMedicineLink',
      data: {
        inquiryNo,
        prescriptionNo: prescriptionNo || '',
      },
      isloading: false,
    }).then((response) => {
      if (response && response.code === '0' && response.result) {
        // 如果提供了成功回调，则调用回调，否则直接跳转
        if (onSuccess) {
          onSuccess(response.result);
        } else {
          window.location.href = response.result;
        }
      } else {
        const errorMessage = response?.message || '获取购药链接失败，请稍后重试';
        StaticToast.error(errorMessage);
        onError?.(new Error(errorMessage));
      }
    }).catch((error) => {
      StaticToast.error('网络异常，请稍后重试');
      onError?.(error);
    });
    return true;
  }
  return false;
};

/**
 * @description 判断当前申请单是否存在购物车链接
 * @returns boolean
 */
export const isShowHyMallCart = (mainBucketNo: string): boolean => {
  try {
    const prescriptionJumpLinkInfo = storage.get(`prescriptionJumpLinkInfo_${mainBucketNo}`) || {};
    const { subjectGroupUrl } = JSON.parse(prescriptionJumpLinkInfo);
    return !!subjectGroupUrl;
  } catch (error) {
    return false;
  }
};

export default commonPay;
